import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useNasabah } from '@/contexts/NasabahContext';
import PinVerifyDialog from '@/components/profile/PinVerifyDialog';

/**
 * Gerbang PIN untuk aksi sensitif nasabah.
 *
 * - `requirePin()` menampilkan dialog PIN bila belum terbuka di sesi ini, lalu
 *   resolve true bila berhasil. Setelah sekali berhasil, aksi "lihat" tidak
 *   diminta PIN lagi selama sesi (kecuali `force`).
 * - `requirePin({ force: true })` selalu meminta PIN (mis. Tarik Saldo).
 * - Bila nasabah belum punya PIN, gerbang dilewati (tidak bisa diverifikasi).
 */
interface RequirePinOptions {
  force?: boolean;
  reason?: string;
}

interface PinGateContextValue {
  /** Minta verifikasi PIN; resolve true bila lolos, false bila dibatalkan. */
  requirePin: (opts?: RequirePinOptions) => Promise<boolean>;
  /** Apakah sesi sudah membuka kunci PIN (untuk gerbang berbasis tampilan). */
  isUnlocked: boolean;
}

const PinGateContext = createContext<PinGateContextValue | undefined>(undefined);

export const PinGateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useNasabah();
  const [unlocked, setUnlocked] = useState(false);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string | undefined>(undefined);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  // Reset kunci sesi saat pengguna berganti (login/logout).
  useEffect(() => {
    setUnlocked(false);
  }, [profile?.userId]);

  const requirePin = useCallback(
    (opts?: RequirePinOptions): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        // Tanpa PIN tersetel, gerbang tidak dapat diverifikasi → lewati.
        if (!profile?.hasPin) {
          setUnlocked(true);
          resolve(true);
          return;
        }
        if (!opts?.force && unlocked) {
          resolve(true);
          return;
        }
        resolverRef.current = resolve;
        setReason(opts?.reason);
        setOpen(true);
      });
    },
    [profile?.hasPin, unlocked],
  );

  const settle = (ok: boolean) => {
    setOpen(false);
    if (ok) setUnlocked(true);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(ok);
  };

  return (
    <PinGateContext.Provider value={{ requirePin, isUnlocked: unlocked }}>
      {children}
      <PinVerifyDialog
        open={open}
        reason={reason}
        onSuccess={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </PinGateContext.Provider>
  );
};

export function usePinGate(): PinGateContextValue {
  const ctx = useContext(PinGateContext);
  if (!ctx) {
    throw new Error('usePinGate harus dipakai di dalam <PinGateProvider>');
  }
  return ctx;
}
